import { AgentModel } from 'api-hooks/agent';
import { BankAccountModel } from 'api-hooks/bank-account';
import { ChequeGiroModel } from 'api-hooks/cheque-giro';
import { EmployeeLiteModel } from 'api-hooks/employee';
import { PaymentMethodModel } from 'api-hooks/payment-method';
import { Expose, Type } from 'class-transformer';
import { TransactionalStatusEnum } from 'common/constants/enum';
import {
  CommonModel,
  CommonParamInput,
} from 'common/repositories/common.model';

export enum InvoicePaymentStatusEnum {
  pending = 'pending',
  finished = 'finished',
  refund = 'refund',
}

export class PaymentableModel extends CommonModel {
  balance: string;
  code: string;
  name: string;

  @Expose({ name: 'account_number' })
  accountNumber: string;
}

export class TransactionableModel {
  id: string;
  status: string;
  number: string;
  acl: string;

  @Expose({ name: 'ref_id' })
  refId: string;

  @Type(() => Number)
  total: number;

  @Expose({ name: 'paid_amount' })
  @Type(() => Number)
  paidAmount: number;

  @Expose({ name: 'due_date' })
  @Type(() => Date)
  dueDate: Date | null;
}

export class PaymentItemModel extends CommonModel {
  @Type(() => Number)
  amount: number;

  @Type(() => Number)
  discount: number;

  @Type(() => Number)
  fee: number;

  @Type(() => Number)
  tax: number;

  @Type(() => Number)
  total: number;

  @Type(() => TransactionableModel)
  transactionable: TransactionableModel;
}

export class ChequeNoteModel extends CommonModel {
  name: string;
  status: string;
  type: string;

  @Expose({ name: 'available_at' })
  @Type(() => Date)
  availableAt: Date;

  @Expose({ name: 'expired_at' })
  @Type(() => Date)
  expiredAt: Date;

  @Expose({ name: 'issued_at' })
  @Type(() => Date)
  issuedAt: Date;

  @Expose({ name: 'reference_number' })
  referenceNumber: string;

  @Expose({ name: 'bank_account' })
  @Type(() => BankAccountModel)
  bankAccount: BankAccountModel;

  @Expose({ name: 'cheque_giro' })
  @Type(() => ChequeGiroModel)
  chequeGiro: ChequeGiroModel;
}

export class PaymentModel extends CommonModel {
  @Type(() => Number)
  amount: number;

  @Type(() => PaymentableModel)
  paymentable: PaymentableModel | null;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Expose({ name: 'payment_method' })
  @Type(() => PaymentMethodModel)
  paymentMethod: PaymentMethodModel;

  @Expose({ name: 'payment_items' })
  @Type(() => PaymentItemModel)
  paymentItems: PaymentItemModel[];

  @Expose({ name: 'cheque_note' })
  @Type(() => ChequeNoteModel)
  chequeNote?: ChequeNoteModel;
}

export class AccountPayableModel extends CommonModel {
  status: TransactionalStatusEnum;
  number: string;
  description: string;

  @Type(() => AgentModel)
  agent: AgentModel;

  @Type(() => PaymentModel)
  payment: PaymentModel;

  @Expose({ name: 'ref_id' })
  refId: string;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Expose({ name: 'is_resolved' })
  isResolved: boolean;
}

export class AgentLiteModel {
  address: string;
  balance: string;
  description: string;
  email: string;
  id: string;
  name: string;
  phone: string;
}

export class TransactionableLiteModel {
  code: string;
  description: string;
  discount: string;
  id: string;
  number: string;
  status: string;

  @Expose({ name: 'agent_id' })
  agentId: string;

  @Expose({ name: 'approved_at' })
  @Type(() => Date)
  approvedAt: Date;

  @Expose({ name: 'completed_at' })
  @Type(() => Date)
  completedAt: Date;

  @Expose({ name: 'created_at' })
  @Type(() => Date)
  createdAt: Date;

  @Expose({ name: 'other_cost' })
  otherCost: string;

  @Expose({ name: 'paid_amount' })
  @Type(() => Number)
  paidAmount: number;

  @Expose({ name: 'payment_channel_id' })
  paymentChannelId: string;

  @Expose({ name: 'payment_status' })
  paymentStatus: string;

  @Expose({ name: 'ref_id' })
  refId: string;

  @Expose({ name: 'rejected_at' })
  @Type(() => Date)
  rejectedAt: Date;

  @Expose({ name: 'sale_ref_id' })
  saleRefId: string;

  @Type(() => Number)
  total: number;

  @Expose({ name: 'tour_id' })
  tourId: string | null;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Expose({ name: 'transaction_type' })
  transactionType: string;
}

export class PaymentItemLiteModel extends CommonModel {
  @Type(() => Number)
  amount: number;

  @Type(() => Number)
  fee: number;

  @Type(() => Number)
  tax: number;

  @Type(() => Number)
  total: number;

  @Type(() => TransactionableLiteModel)
  transactionable: TransactionableLiteModel;
}

export class PaymentMethodLiteModel extends CommonModel {
  type: string;
}

export class PaymentLiteModel {
  @Type(() => Number)
  amount: number;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Expose({ name: 'payment_method' })
  @Type(() => PaymentMethodLiteModel)
  paymentMethod: PaymentMethodLiteModel;

  @Expose({ name: 'payment_items' })
  @Type(() => PaymentItemLiteModel)
  paymentItems: PaymentItemLiteModel[];

  @Expose({ name: 'created_at' })
  @Type(() => Date)
  createdAt: Date;

  @Expose({ name: 'updated_at' })
  @Type(() => Date)
  updatedAt: Date;
}

export class AccountPayableLiteModel extends CommonModel {
  description: string;
  number: string;
  status: string;

  @Expose({ name: 'external_id' })
  externalId: string;

  @Type(() => AgentLiteModel)
  agent: AgentLiteModel;

  @Type(() => PaymentLiteModel)
  payment: PaymentLiteModel;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Expose({ name: 'created_by' })
  @Type(() => EmployeeLiteModel)
  createdBy: EmployeeLiteModel;
}

export class AccountPayableAgentModel extends CommonModel {
  @Expose({ name: 'due_date' })
  @Type(() => Date)
  dueDate: Date | null;

  @Expose({ name: 'ref_id' })
  refId: string;

  @Expose({ name: 'invoice_number' })
  invoiceNumber: string;

  @Expose({ name: 'paid_amount' })
  @Type(() => Number)
  paidAmount: number;

  @Expose({ name: 'payment_status' })
  paymentStatus: string;

  @Type(() => Number)
  total: number;
}

export class getAccountPayablesInput {
  params: CommonParamInput;
}

export class getAccountPayableInput {
  id: string;
}

export class getAccountPayableAgentInput {
  id: string;
  params?: { payment_status?: string };
  othersKeys?: string[];
}

//mutation
export class AccountPayableMutationInput {
  refId: string;
  transactionAt: Date;
  needApproval: boolean;
  agentId: string;
  description: string;
  paymentMethodId: string;
  paymentableId: string;
  chequeNote: object;
  paymentItems: {
    amount: number;
    tax: number;
    discount: number;
    fee: number;
  }[];
}
export class AccountPayableUpdateInput {
  id: string;
  body: AccountPayableMutationInput;
}
export class AccountPayableVoidInput {
  id: string;
}
